<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, watch, onMounted, ref } from 'vue'
import { useLogger } from 'vue-logger-plugin'
import { TextBox } from '@progress/kendo-vue-inputs'
import { DropDownList } from '@progress/kendo-vue-dropdowns'
import type { IAoeNewUserWizard } from '../../types/WizardTypes'
import { SecurityQuestionGet } from '../../api/Security.ts'
import { BusinessNameUpdate } from '../../api/Business.ts'
import { PwsSecurityResponseSave, BeeSecurityResponseGetConnector, type PwsSecurityResponseSaveRequest } from '../../api/ConsultantCorner.ts'
import type { ISecurityQuestion } from '../../types/SecurityTypes.ts'
import { GetBeeNumberAuthenicatedUser, GetCountryCode, GetPwsBaseUrl } from '../../stores/SettingsStore.ts'
import { getCleanString } from '../../utils/businessNameHelper.ts'

const { t } = useI18n()
const logger = useLogger()

// Props defined explicitly to avoid TypeScript errors
const props = defineProps({
  formData: {
    type: Object,
    required: true
  },
  onUpdate: {
    type: Function,
    required: true
  },
  isValid: {
    type: Boolean,
    required: true
  }
})

// Emit events to parent component
const emit = defineEmits(['update:isValid', 'skip-french-contribution', 'next-step', 'skip-to-step'])

// State for handling security questions loading
const isLoading = ref(false)
const loadError = ref('')

// State for handling response saving feedback
const saveSuccess = ref(false)
const saveError = ref('')

// Security questions form data
interface SecurityQuestionDropdownItem {
  text: string
  value: number
}

const securityFormData = ref({
  selectedQuestionNumber: null as number | null,
  genericQuestionResponse: '',
  customQuestion: '',
  customQuestionResponse: ''
})

const securityErrors = ref<Record<string, string>>({})
const predefinedQuestions = ref<SecurityQuestionDropdownItem[]>([])

// State for handling business name update feedback
const businessNameUpdateSuccess = ref(false)
const businessNameUpdateError = ref('')
const isBusinessNameUpdating = ref(false)
const personalUrl = ref('')
const countryCode = ref('')

// Check if both security questions are answered
const areBothQuestionsAnswered = computed(() => {
  return (
    securityFormData.value.selectedQuestionNumber !== null &&
    securityFormData.value.genericQuestionResponse.trim() !== '' &&
    securityFormData.value.customQuestion.trim() !== '' &&
    securityFormData.value.customQuestionResponse.trim() !== ''
  )
})

// Check if business name is valid for update (non-empty and ASCII only)
const isBusinessNameValid = computed(() => {
  const trimmed = props.formData.businessName.trim()
  return trimmed !== '' && /^[\x00-\x7F]+$/.test(trimmed)
})

// Track if business name has been successfully updated
const isBusinessNameUpdated = ref(false)

// Check if the step is valid
const isStepValid = computed(() => {
  return isBusinessNameUpdated.value && areBothQuestionsAnswered.value
})

// Calculate validation message to show in tooltip
const validationMessage = computed(() => {
  if (!isBusinessNameUpdated.value && !areBothQuestionsAnswered.value) {
    return t('validators.businessNameAndSecurityQuestionRequired')
  } else if (!isBusinessNameUpdated.value) {
    return t('validators.businessNameRequired')
  } else if (!areBothQuestionsAnswered.value) {
    return t('validators.securityQuestionRequired')
  }
  return ''
})

// Update step validity when it changes
watch(isStepValid, (newValue) => {
  emit('update:isValid', newValue)
})

// Load security questions from API
async function loadSecurityQuestions() {
  isLoading.value = true
  loadError.value = ''

  try {
    const response = await SecurityQuestionGet()

    if (response && (response as any).items) {
      predefinedQuestions.value = (response as any).items.map((q: ISecurityQuestion) => ({
        text: (q as any).securityQuestion,
        value: (q as any).questionsNumber
      }))
    }
  } catch (error) {
    logger.error('Error loading security questions:', error)
    loadError.value = t('consultantCorner.securityQuestions.loadError')
  } finally {
    isLoading.value = false
  }
}

// Load saved security responses to prefill the form (matching ContactDetailsScreen and CCMyAccount flow)
async function loadSavedSecurityResponses() {
  try {
    // Pass true to get answers back - only NewUserWizard needs to display/edit them
    const securityResponse = await BeeSecurityResponseGetConnector(true)

    if (securityResponse && securityResponse.items && securityResponse.items.length > 0) {
      // Find the generic question (questionNumber should be set for predefined questions)
      const genericQuestion = securityResponse.items.find(item => item.questionNumber && item.questionNumber > 0)
      if (genericQuestion) {
        securityFormData.value.selectedQuestionNumber = genericQuestion.questionNumber
        // Also load the answer (responseText) so user can see/edit it
        securityFormData.value.genericQuestionResponse = genericQuestion.responseText || ''
      }

      // Find the custom question (customQuestion should be populated)
      const customQuestion = securityResponse.items.find(item => item.customQuestion && item.customQuestion.trim() !== '')
      if (customQuestion) {
        securityFormData.value.customQuestion = customQuestion.customQuestion || ''
        // Also load the answer - check responseText first, then customQuestionResponse
        securityFormData.value.customQuestionResponse = customQuestion.responseText || customQuestion.customQuestionResponse || ''
      }

      logger.debug('Prefilled security responses from saved data:', securityFormData.value)
    }
  } catch (error) {
    // No need to show error - if there are no saved questions, user can answer fresh
    logger.error('Failed to load saved security responses:', error)
  }
}

// Update business name
async function updateBusinessName() {
  try {
    // Reset status
    businessNameUpdateSuccess.value = false
    businessNameUpdateError.value = ''
    isBusinessNameUpdating.value = true

    // Get the authenticated user's BEE number
    const beeNumber = await GetBeeNumberAuthenicatedUser()

    if (!beeNumber) {
      businessNameUpdateError.value = t('errors.updatingBusinessName')
      logger.error('Error updating business name: No BEE number available')
      return false
    }

    const trimmedBusinessName = props.formData.businessName.trim()
    if (/[^\x00-\x7F]/.test(trimmedBusinessName)) {
      businessNameUpdateError.value = t('consultantCorner.companyName.BusinessNameInvalidCharacters')
      isBusinessNameUpdated.value = false
      return false
    }

    const cleanedBusinessName = getCleanString(props.formData.businessName)
    if (cleanedBusinessName !== props.formData.businessName) {
      props.onUpdate({ businessName: cleanedBusinessName })
    }

    const response = await BusinessNameUpdate(beeNumber, cleanedBusinessName)

    console.log("Business name update response:", response)
    // Check if response exists and if there's an error code for business name already exists
    if (response && response.responseStatus && response.responseStatus.errorCode === 'BusinessNameAlreadyExists') {
      businessNameUpdateError.value = t('errors.businessNameAlreadyExists')
      console.log("Business name already exists:", response)
      logger.error('Business name already exists:', response)
      isBusinessNameUpdated.value = false
      return false
    }

    // Check if response exists and if ErrorCode and Message are empty or null (indicating success)
    if (response == null || response.responseStatus == null) {
      businessNameUpdateSuccess.value = true
      isBusinessNameUpdated.value = true
      logger.info('Business name updated successfully')
      return true
    } else {
      businessNameUpdateError.value = t('errors.updatingBusinessName')
      logger.error('Error updating business name:', response)
      isBusinessNameUpdated.value = false
      return false
    }
  } catch (error) {
    businessNameUpdateError.value = t('errors.updatingBusinessName')
    logger.error('Error updating business name:', error)
    isBusinessNameUpdated.value = false
    return false
  } finally {
    isBusinessNameUpdating.value = false
  }
}

// Handle dropdown question change
const onQuestionChange = (event: any) => {
  securityFormData.value.selectedQuestionNumber = event.value
  if (securityErrors.value.selectedQuestionNumber) {
    delete securityErrors.value.selectedQuestionNumber
  }
}

// Validate security questions form
const validateSecurityQuestions = (): boolean => {
  securityErrors.value = {}

  if (!securityFormData.value.selectedQuestionNumber) {
    securityErrors.value.selectedQuestionNumber = t('consultantCorner.securityQuestions.questionRequired')
  }

  if (!securityFormData.value.genericQuestionResponse.trim()) {
    securityErrors.value.genericQuestionResponse = t('consultantCorner.securityQuestions.answerRequired')
  }

  if (!securityFormData.value.customQuestion.trim()) {
    securityErrors.value.customQuestion = t('consultantCorner.securityQuestions.customQuestionRequired')
  }

  if (!securityFormData.value.customQuestionResponse.trim()) {
    securityErrors.value.customQuestionResponse = t('consultantCorner.securityQuestions.customAnswerRequired')
  }

  return Object.keys(securityErrors.value).length === 0
}

// Save security question responses
async function saveSecurityResponses() {
  try {
    // Reset status
    saveSuccess.value = false
    saveError.value = ''

    // Validate form
    if (!validateSecurityQuestions()) {
      return
    }

    // Create request object
    const request: PwsSecurityResponseSaveRequest = {
      GenericQuestionNumber: securityFormData.value.selectedQuestionNumber ?? 0,
      GenericQuestionResponse: securityFormData.value.genericQuestionResponse,
      CustomQuestion: securityFormData.value.customQuestion,
      CustomQuestionResponse: securityFormData.value.customQuestionResponse
    }

    // Save responses to API
    await PwsSecurityResponseSave(request)

    // Show success message
    saveSuccess.value = true

    // Log success
    logger.info('Security question responses saved successfully')

    // Auto-hide success message after 3 seconds
    setTimeout(() => {
      saveSuccess.value = false
    }, 3000)
  } catch (error) {
    // Handle error
    saveError.value = t('consultantCorner.securityQuestions.saveError')
    logger.error('Error saving security responses:', error)

    // Auto-hide error message after 5 seconds
    setTimeout(() => {
      saveError.value = ''
    }, 5000)
  }
}

// Save security question responses when continuing to next step
const saveBeforeContinue = async () => {
  if (isStepValid.value) {
    // Save security responses
    await saveSecurityResponses()

    // IMPORTANT: FrenchContribution step is disabled, so we don't need to check country code
    // or emit any events related to French Contribution
    // This prevents the FrenchContributionSave API call which is failing

    // Just log that we've completed the business step
    logger.debug('Business step completed successfully')
  }
}

// Expose the method so the parent component can call it
defineExpose({
  saveBeforeContinue
})

// Function to check if business name is already set
async function checkBusinessNameExists() {
  // Don't make API calls that would cause automatic redirection
  // Just get the country code for reference
  try {
    isLoading.value = true
    countryCode.value = await GetCountryCode() || ''

    // Don't emit any skip-to-step events here to prevent automatic navigation
    logger.debug('BusinessStep initialized with country code:', countryCode.value)
  } catch (error) {
    logger.error('Error getting country code:', error)
  } finally {
    isLoading.value = false
  }
}

// Function to update personal URL based on business name
async function updatePersonalUrl(businessName: string) {
  if (businessName && businessName.trim() !== '') {
    const businessNameForUrl = businessName.trim().toLowerCase().replace(/\s+/g, '-')
    const baseUrl = await GetPwsBaseUrl()
    personalUrl.value = `${baseUrl}${businessNameForUrl}`
  } else {
    personalUrl.value = ''
  }
}

// Watch for changes in business name to update personal URL
watch(() => props.formData.businessName, (newValue) => {
  businessNameUpdateError.value = ''

  if (/[^\x00-\x7F]/.test(newValue.trim())) {
    businessNameUpdateError.value = t('consultantCorner.companyName.BusinessNameInvalidCharacters')
    return
  }

  const cleanedBusinessName = getCleanString(newValue)

  if (cleanedBusinessName !== newValue) {
    props.onUpdate({ businessName: cleanedBusinessName })
    return
  }

  updatePersonalUrl(cleanedBusinessName)
}, { immediate: true })

// Load security questions and country code when mounting the component
onMounted(async () => {
  // Only load security questions if they're not already loaded
  await loadSecurityQuestions()

  // Load saved security responses to prefill the form (from ContactDetailsScreen or previous attempts)
  await loadSavedSecurityResponses()

  // Only get country code if we don't already have it
  if (!countryCode.value) {
    try {
      countryCode.value = await GetCountryCode()
    } catch (error) {
      logger.error('Error getting country code:', error)
      // Default to empty string if there's an error
      countryCode.value = ''
    }
  }

  // Skip checking business name existence to prevent unwanted API calls
  // Just validate the current state based on existing data

  // Validate step when component loads
  if (isBusinessNameUpdated.value && areBothQuestionsAnswered.value) {
    emit('update:isValid', true)
  }
})
</script>

<template>
  <div class="wizard-step">
    <div class="wizard-step-header">
      <h3>{{ t('labels.BusinessInfoHeader') }}</h3>
      <p>{{ t('labels.BusinessInfoSubHeader') }}</p>
    </div>

    <div class="form-section">
      <!-- Business Name Row with label and input aligned horizontally -->
      <div class="business-name-row">
        <div class="business-name-label">
          <label class="form-label">{{ t('labels.BusinessName') }} *</label>
        </div>
        <div class="business-name-input">
          <TextBox
            v-model="formData.businessName"
            class="k-textbox"
            :placeholder="t('labels.BusinessNamePlaceholder')"
            data-testid="businessName"
          />
        </div>
        <div class="business-name-update-button">
          <button
            class="btn primaryBtn btn-sm"
            @click="updateBusinessName"
            :disabled="!isBusinessNameValid || isBusinessNameUpdating"
          >
           {{ t('actions.UpdateBusinessName') }}
          </button>
        </div>
      </div>

      <!-- Alerts appear below the input row -->
      <div class="business-name-alerts">
        <!-- Success alert for business name update -->
        <div v-if="businessNameUpdateSuccess" class="alert alert-success alert-dismissible fade show" role="alert">
          <strong>{{ t('labels.Success') }}</strong> {{ t('messages.businessNameUpdated') }}
          <button type="button" class="btn-close" @click="businessNameUpdateSuccess = false" :aria-label="t('buttons.Close')"></button>
        </div>

        <!-- Error alert for business name update failure -->
        <div v-if="businessNameUpdateError" class="alert alert-danger alert-dismissible fade show" role="alert">
          <strong>{{ t('labels.Error') }}</strong> {{ businessNameUpdateError }}
          <button type="button" class="btn-close" @click="businessNameUpdateError = ''" :aria-label="t('buttons.Close')"></button>
        </div>
      </div>

      <!-- Personal URL display appears below alerts -->
      <div v-if="personalUrl" class="personal-url-row">
        <label class="form-label">{{ t('labels.PersonalURL') }}</label>
        <div class="personal-url-display">
          {{ personalUrl }}
        </div>
      </div>

      <div class="security-questions-section">
        <h4>{{ t('consultantCorner.securityQuestions.header') }}</h4>
        <p>{{ t('consultantCorner.securityQuestions.subHeader') }}</p>

        <!-- Success alert for security questions saved -->
        <div v-if="saveSuccess" class="alert alert-success alert-dismissible fade show" role="alert">
          <strong>{{ t('labels.Success') }}</strong> {{ t('consultantCorner.securityQuestions.saveSuccess') }}
          <button type="button" class="btn-close" @click="saveSuccess = false" :aria-label="t('buttons.Close')"></button>
        </div>

        <!-- Error alert for security questions save failure -->
        <div v-if="saveError" class="alert alert-danger alert-dismissible fade show" role="alert">
          <strong>{{ t('labels.Error') }}</strong> {{ saveError }}
          <button type="button" class="btn-close" @click="saveError = ''" :aria-label="t('buttons.Close')"></button>
        </div>

        <div v-if="isLoading" class="loading-indicator">
          {{ t('consultantCorner.securityQuestions.loading') }}
        </div>

        <div v-else-if="loadError" class="validation-message">
          {{ loadError }}
          <button @click="loadSecurityQuestions" class="reload-button">
            {{ t('consultantCorner.securityQuestions.tryAgain') }}
          </button>
        </div>

        <div v-else-if="predefinedQuestions.length === 0" class="no-questions">
          {{ t('labels.NoSecurityQuestions') }}
        </div>

        <template v-else>
          <!-- Question 1: Generic dropdown question -->
          <div class="question-group">
            <h5>{{ t('consultantCorner.securityQuestions.question1Label') }}</h5>
            <div class="form-row">
              <label class="form-label">{{ t('consultantCorner.securityQuestions.selectQuestion') }}</label>
              <DropDownList
                :data-items="predefinedQuestions"
                :value="securityFormData.selectedQuestionNumber"
                :value-primitive="true"
                @change="onQuestionChange"
                text-field="text"
                value-field="value"
                class="k-dropdown"
                :class="{ 'is-invalid': securityErrors.selectedQuestionNumber }"
                :default-item="{ text: t('consultantCorner.securityQuestions.selectQuestionPlaceholder'), value: null }"
              />
              <div v-if="securityErrors.selectedQuestionNumber" class="validation-message">
                {{ securityErrors.selectedQuestionNumber }}
              </div>
            </div>
            <div class="form-row">
              <label class="form-label">{{ t('consultantCorner.securityQuestions.answerLabel') }}</label>
              <TextBox
                v-model="securityFormData.genericQuestionResponse"
                class="k-textbox"
                :class="{ 'is-invalid': securityErrors.genericQuestionResponse }"
                :placeholder="t('consultantCorner.securityQuestions.answerPlaceholder')"
              />
              <div v-if="securityErrors.genericQuestionResponse" class="validation-message">
                {{ securityErrors.genericQuestionResponse }}
              </div>
            </div>
          </div>

          <!-- Question 2: Custom question -->
          <div class="question-group">
            <h5>{{ t('consultantCorner.securityQuestions.question2Label') }}</h5>
            <div class="form-row">
              <label class="form-label">{{ t('consultantCorner.securityQuestions.customQuestionLabel') }}</label>
              <TextBox
                v-model="securityFormData.customQuestion"
                class="k-textbox"
                :class="{ 'is-invalid': securityErrors.customQuestion }"
                :placeholder="t('consultantCorner.securityQuestions.customQuestionPlaceholder')"
              />
              <div v-if="securityErrors.customQuestion" class="validation-message">
                {{ securityErrors.customQuestion }}
              </div>
            </div>
            <div class="form-row">
              <label class="form-label">{{ t('consultantCorner.securityQuestions.answerLabel') }}</label>
              <TextBox
                v-model="securityFormData.customQuestionResponse"
                class="k-textbox"
                :class="{ 'is-invalid': securityErrors.customQuestionResponse }"
                :placeholder="t('consultantCorner.securityQuestions.answerPlaceholder')"
              />
              <div v-if="securityErrors.customQuestionResponse" class="validation-message">
                {{ securityErrors.customQuestionResponse }}
              </div>
            </div>
          </div>

          <!-- Validation message for business step -->
          <div v-if="!isStepValid" class="step-validation-message">
            {{ t('validators.stepRequirementsHeader') }}
            <ul>
              <li v-if="!isBusinessNameUpdated" class="validation-item">
                {{ t('validators.businessNameRequired') }}
              </li>
              <li v-if="!areBothQuestionsAnswered" class="validation-item">
                {{ t('validators.securityQuestionRequired') }}
              </li>
            </ul>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.wizard-step {
  margin-bottom: 25px;
}

.wizard-step-header {
  margin-bottom: 16px;
}

.wizard-step-header h3 {
  @include typography-h4;
  @include font-weight-semibold;
  margin-bottom: 6px;
}

.wizard-step-header p {
  @include typography-body;
  color: #666;
  margin-bottom: 10px;
}

.form-section {
  margin-bottom: 25px;
}

.form-row {
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
}

.form-label {
  @include typography-body;
  @include font-weight-medium;
  display: block;
  margin-bottom: 6px;
  width: 100%;
  word-wrap: break-word;
  white-space: normal;
}

/* Business name section layout */
.business-name-row {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  min-height: 34px;
  flex-wrap: wrap;
}

.business-name-label {
  width: 120px;
  flex-shrink: 0;
}

.business-name-label .form-label {
  margin-bottom: 0;
  line-height: 34px;
  height: 34px;
  display: flex;
  align-items: center;
}

.business-name-input {
  flex: 1;
  margin-right: 15px;
  min-width: 200px;
}

.business-name-update-button {
  width: auto;
  flex-shrink: 0;
}

.business-name-update-button .btn {
  @include typography-body;
  height: 34px;
  padding: 0 15px;
  white-space: nowrap;
  min-width: 180px;
  color: var(--vt-c-white-soft);
  background-color: #2a08a5;
  border: 1px solid #cccccc;
  border-radius: $radius-sm;
  display: flex;
  align-items: center;
  @include font-weight-medium;
  justify-content: center;
  line-height: 1;
}

.business-name-update-button .btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  background-color: rgba(#2a08a5, 0.7);
}

.business-name-alerts {
  margin: $spacing-3 0;
}

/* Fix vertical alignment of input boxes with labels */
.k-textbox {
  display: block;
  width: 100%;
  @include typography-body;
  height: 34px;
  line-height: normal;
  display: flex;
  align-items: center;
}

.k-input-inner {
  height: 100%;
  padding: 0.3rem 0.6rem;
  vertical-align: middle;
}

/* Ensure all inputs have consistent height and padding */
input.k-input-inner {
  height: 34px;
  box-sizing: border-box;
  vertical-align: middle;
  @include typography-body;
  display: flex;
  align-items: center;
}

.security-questions-section {
  margin-top: 25px;
}

.security-question-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  display: block;
}

.security-questions-section h4 {
  @include typography-body-large;
  @include font-weight-semibold;
  margin-bottom: 8px;
}

.security-questions-section p {
  @include typography-body;
  margin-bottom: 16px;
  color: #666;
}

.question-group {
  margin-bottom: 20px;
  padding: $spacing-4;
  background-color: var(--vt-c-white-soft);
  border-radius: $radius-md;
  border: 1px solid #e9ecef;
}

.question-group h5 {
  @include typography-body;
  @include font-weight-semibold;
  color: #333;
  margin-bottom: 12px;
  border-bottom: 2px solid #2a08a5;
  padding-bottom: 6px;
}

:deep(.k-dropdown),
:deep(.k-textbox) {
  @include typography-body;
  width: 100%;
  height: 34px;
}

:deep(.k-dropdown .k-dropdown-wrap) {
  height: 34px;
}

:deep(.k-dropdown .k-input) {
  height: 32px;
  line-height: 32px;
}


.k-textbox.is-invalid,
.k-dropdown.is-invalid {
  border-color: #e53935;
}

.validation-message {
  @include typography-small;
  color: #e53935;
  margin-top: 4px;
}

.step-validation-message {
  margin-top: 16px;
  padding: $spacing-2 $spacing-3;
  background-color: #fff3e0;
  @include typography-small;
  border-left: 4px solid #ff9800;
  border-radius: $radius-sm;
}

.validation-item {
  @include typography-small;
  margin-top: 4px;
  color: #e53935;
}

.loading-indicator {
  @include typography-body;
  margin: $spacing-4 0;
  font-style: italic;
  color: #666;
}

.no-questions {
  @include typography-body;
  margin: $spacing-4 0;
  color: #666;
}

.reload-button {
  margin-left: 8px;
  padding: $spacing-1 $spacing-2;
  background-color: var(--vt-c-white-soft);
  border: 1px solid #e0e0e0;
  @include typography-small;
  border-radius: $radius-sm;
  cursor: pointer;
}

.form-actions {
  margin-top: 16px;
  display: flex;
  justify-content: flex-start;
}

/* Personal URL styling */
.personal-url-row {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e9ecef;
}

.personal-url-display {
  padding: $spacing-2;
  background-color: var(--vt-c-white-soft);
  border: 1px solid #e0e0e0;
  border-radius: $radius-sm;
  @include typography-small;
  font-family: monospace;
  word-break: break-all;
}

.alert {
  @include typography-small;
  padding: $spacing-2-5 $spacing-4;
  margin-bottom: 12px;
  border-radius: $radius-sm;
}

.alert-success {
  background-color: #d4edda;
  border-color: #c3e6cb;
  color: #155724;
}

.alert-danger {
  background-color: #f8d7da;
  border-color: #f5c6cb;
  color: #721c24;
}

.alert-dismissible {
  position: relative;
  padding-right: 36px;
}

.btn-close {
  position: absolute;
  top: 10px;
  right: 10px;
  background: transparent;
  @include typography-h4;
  @include font-weight-bold;
  border: 0;
  cursor: pointer;
  line-height: 1;
  opacity: 0.5;
}
</style>
